import type { TFunction } from 'i18next';

import type {
  DisplayCreative,
  FacebookDisplayCreative,
  ICreativeAuditStatus,
  IFacebookAdReviewFeedback,
  TCreativeAuditStatus,
} from '@feathr/blackbox';
import type { TCreativeStatus, TMetaFeedPlacement } from '@feathr/components';
import { isMaskedInMetaFeed } from '@feathr/components';

/**
 * Result of determining a creative's status for display.
 */
export interface ICreativeStatusResult {
  /** The visual status indicator: 'ready', 'warning', or 'error' */
  status: TCreativeStatus;
  /** Tooltip text describing the status */
  tooltip: string;
}

/**
 * Maps display creative audit status to visual status indicator.
 *
 * @param statusValue - The audit status value (e.g., 'approved', 'rejected')
 * @returns The visual status: 'ready', 'warning', or 'error'
 */
function mapDisplayAuditStatusToVisual(
  statusValue: TCreativeAuditStatus | undefined,
): TCreativeStatus {
  switch (statusValue) {
    case 'approved':
      return 'ready';

    case 'rejected':

    case 'unauditable':
      return 'error';

    default:
      return 'warning';
  }
}

/**
 * Gets the display label for an audit status.
 *
 * @param statusValue - The audit status value
 * @param t - Translation function
 * @returns Translated status label
 */
function getDisplayAuditStatusLabel(
  statusValue: TCreativeAuditStatus | undefined,
  t: TFunction,
): string {
  switch (statusValue) {
    case 'not audited':
      return t('Pending review');

    case 'pending':
      return t('Pending review');

    case 'approved':
      return t('Approved');

    case 'rejected':
      return t('Rejected');

    case 'unauditable':
      return t('Not reviewable');

    default:
      return t('Pending review');
  }
}

/**
 * Formats a single audit status entry for tooltip display.
 *
 * @param status - The audit status entry
 * @param t - Translation function
 * @returns Formatted string like "Google: Approved" or "AppNexus: Rejected (reason)"
 */
function formatAuditStatusEntry(status: ICreativeAuditStatus, t: TFunction): string {
  const label = getDisplayAuditStatusLabel(status.status, t);
  if (status.reason) {
    return `${status.auditor}: ${label} (${status.reason})`;
  }
  return `${status.auditor}: ${label}`;
}

/**
 * Builds tooltip text from display creative audit statuses.
 *
 * @param statuses - Array of audit statuses from different auditors
 * @param uniqueStatuses - Pre-computed unique status values
 * @param t - Translation function
 * @returns Formatted tooltip string
 */
function buildDisplayStatusTooltip(
  statuses: ICreativeAuditStatus[],
  uniqueStatuses: TCreativeAuditStatus[],
  t: TFunction,
): string {
  if (uniqueStatuses.length === 0) {
    return t('Pending review');
  }

  // Show full breakdown when there are different statuses across auditors
  if (uniqueStatuses.length > 1) {
    return statuses
      .filter((s) => s.status)
      .map((s) => formatAuditStatusEntry(s, t))
      .join('\n');
  }

  const primaryStatus = uniqueStatuses[0];
  const label = getDisplayAuditStatusLabel(primaryStatus, t);

  if (primaryStatus === 'rejected') {
    const reasons = statuses
      .filter((s) => s.status === 'rejected' && s.reason)
      .map((s) => s.reason);
    if (reasons.length > 0) {
      return `${label}: ${reasons.join(', ')}`;
    }
  }

  return label;
}

/**
 * Gets the creative status for display (TTD) creatives.
 *
 * Uses the `audit_statuses` array to determine status:
 * - Empty or 'not audited' -> ready, "Pending review"
 * - 'pending' -> ready, "Pending review"
 * - 'approved' -> ready, "Approved"
 * - 'rejected' -> error, "Rejected: [reason]"
 * - 'unauditable' -> warning, "Not reviewable"
 * - Mixed statuses -> error (if any rejected)
 *
 * @param creative - The display creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getDisplayCreativeStatus(
  creative: DisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  const statuses = (creative.get('audit_statuses') as ICreativeAuditStatus[] | undefined) ?? [];
  const uniqueStatuses = [...new Set(statuses.map((s) => s.status))].filter(
    (s): s is TCreativeAuditStatus => Boolean(s),
  );

  let visualStatus: TCreativeStatus;
  if (uniqueStatuses.length > 1) {
    // Only show error if there's a rejection; otherwise show warning for pending
    visualStatus = uniqueStatuses.includes('rejected') ? 'error' : 'warning';
  } else if (uniqueStatuses.length === 0) {
    visualStatus = 'ready';
  } else {
    visualStatus = mapDisplayAuditStatusToVisual(uniqueStatuses[0]);
  }

  return {
    status: visualStatus,
    tooltip: buildDisplayStatusTooltip(statuses, uniqueStatuses, t),
  };
}

/**
 * Maps Meta ad_status to visual status indicator.
 *
 * @param adStatus - The Meta ad status value
 * @returns The visual status: 'ready', 'warning', or 'error'
 */
export function mapMetaAdStatusToVisual(adStatus: string | undefined): TCreativeStatus {
  switch (adStatus) {
    case 'DISAPPROVED':

    case 'ARCHIVED':
      return 'error';

    case 'ACTIVE':
      return 'ready';

    case 'PAUSED':

    case 'WITH_ISSUES':

    case 'PENDING_REVIEW':

    case 'IN_PROCESS':

    default:
      return 'warning';
  }
}

/**
 * Gets the display label for a Meta ad status.
 *
 * @param adStatus - The Meta ad status value
 * @param t - Translation function
 * @returns Translated status label
 */
export function getMetaAdStatusLabel(adStatus: string | undefined, t: TFunction): string {
  switch (adStatus) {
    case 'ACTIVE':
      return t('Approved');

    case 'PENDING_REVIEW':

    case 'IN_PROCESS':
      return t('Pending review');

    case 'DISAPPROVED':
      return t('Rejected');

    case 'WITH_ISSUES':
      return t('Has issues');

    case 'PAUSED':
      return t('Paused');

    case 'ARCHIVED':
      return t('Archived');

    default:
      return t('Pending review');
  }
}

/**
 * Extracts all feedback text from Meta ad review feedback object.
 * Collects feedback from facebook, instagram, and general feedback sources,
 * deduplicates them, and returns a combined string.
 *
 * @param adReviewFeedback - The feedback object from Meta
 * @returns All unique feedback messages joined by "; ", or undefined if none
 */
export function extractMetaFeedback(
  adReviewFeedback: IFacebookAdReviewFeedback | undefined,
): string | undefined {
  if (!adReviewFeedback) {
    return undefined;
  }

  // Each IFacebookAdReviewFeedback field (facebook, global, instagram, feedback)
  // is a Record<string, string>. Collect every non-empty value across all
  // sources and dedupe, so identical messages from multiple surfaces only
  // appear once.
  const uniqueFeedback = new Set(
    Object.values(adReviewFeedback)
      .filter((source): source is Record<string, string> => Boolean(source))
      .flatMap((source) => Object.values(source).filter(Boolean)),
  );

  if (uniqueFeedback.size === 0) {
    return undefined;
  }

  return Array.from(uniqueFeedback).join('; ');
}

/**
 * Gets the creative status for Meta (Facebook/Instagram) creatives.
 *
 * Uses the `ad_status` field to determine status:
 * - ACTIVE -> ready, "Approved"
 * - PENDING_REVIEW / IN_PROCESS -> warning, "Pending review"
 * - PAUSED -> warning, "Paused"
 * - WITH_ISSUES -> warning, "Has issues: [feedback]"
 * - DISAPPROVED -> error, "Rejected: [feedback]"
 * - ARCHIVED -> error, "Archived"
 * - undefined -> warning, "Pending review"
 *
 * @param creative - The Meta creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getMetaCreativeStatus(
  creative: FacebookDisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  const adStatus = creative.get('ad_status');
  const adReviewFeedback = creative.get('ad_review_feedback');

  const visualStatus = mapMetaAdStatusToVisual(adStatus);
  const label = getMetaAdStatusLabel(adStatus, t);

  let tooltip = label;
  if ((adStatus === 'DISAPPROVED' || adStatus === 'WITH_ISSUES') && adReviewFeedback) {
    const feedback = extractMetaFeedback(adReviewFeedback);
    if (feedback) {
      tooltip = `${label}: ${feedback}`;
    }
  }

  return {
    status: visualStatus,
    tooltip,
  };
}

/**
 * Gets the creative status for any creative type.
 *
 * Automatically detects whether the creative is a Meta or Display creative
 * and delegates to the appropriate status function.
 *
 * @param creative - The creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getCreativeStatus(
  creative: DisplayCreative | FacebookDisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  if (creative.channel === 'meta') {
    return getMetaCreativeStatus(creative as FacebookDisplayCreative, t);
  }
  return getDisplayCreativeStatus(creative, t);
}

// =============================================================================
// Meta feed crop warnings
// =============================================================================
// Single source of truth for the Meta crop messaging so the voice stays
// consistent across the upload block, the creative card tooltip, the preview
// alert, and the review step. A creative taller than the feed's limit IS
// cropped when shown in Meta and Instagram feeds — the ad may still look fine,
// but important parts can be cut off.
//
// Which creatives get cropped comes from `isMaskedInMetaFeed`, the same rule the
// preview windows size their media container from, so the warning and the
// preview can never disagree. 9:16 is cropped in both feeds; a 4:5 image is
// cropped in the Facebook feed, where the link card masks it to a square.
// =============================================================================

/**
 * Names the feeds that crop a creative, for use inside the warning copy. Which
 * feeds those are depends on the asset: the Facebook feed masks any image
 * taller than square, while the Instagram feed only masks past 4:5.
 *
 * @param t - Translation function.
 * @param feeds - The feeds that crop the creative.
 * @returns The localized feed name(s), e.g. "the Facebook feed".
 */
function getMaskedFeedsLabel(t: TFunction, feeds: TMetaFeedPlacement[]): string {
  // Instagram alone is unreachable: its 4:5 limit is never stricter than the
  // Facebook feed's, so a cropped creative is always cropped on Facebook too.
  return feeds.length > 1 ? t('the Facebook and Instagram feeds') : t('the Facebook feed');
}

/**
 * Title for the single-creative crop warning (preview alert).
 *
 * @param t - Translation function.
 * @param feeds - The feeds that crop the creative.
 * @returns The localized warning title.
 */
export function getMetaPortraitWarningTitle(t: TFunction, feeds: TMetaFeedPlacement[]): string {
  return t('Part of this creative is cut off in {{feeds}}', {
    feeds: getMaskedFeedsLabel(t, feeds),
  });
}

/**
 * Title for the campaign-level review-step warning. Pluralizes and includes the
 * number of affected creatives ("1 creative is" vs "2 creatives are").
 *
 * @param t - Translation function.
 * @param count - Number of affected 9:16 Meta creatives.
 * @returns The localized, pluralized review-step title.
 */
export function getMetaPortraitReviewTitle(t: TFunction, count: number): string {
  return t('{{count}} creatives are cropped in Meta feeds', { count });
}

/**
 * The error shown when a 9:16 upload is blocked from the Meta channel.
 *
 * @param t - Translation function.
 * @param filename - Optional file name to attribute the error to.
 * @returns The localized block message.
 */
export function getMetaPortraitUploadError(t: TFunction, filename?: string): string {
  if (filename) {
    return t(
      '"{{filename}}" is 9:16 (1080×1920), which is cropped in Meta and Instagram feeds. Use 1:1 (1080×1080) for images, or 4:5 (1080×1350) for video.',
      { filename },
    );
  }
  return t(
    '9:16 (1080×1920) creatives are cropped in Meta and Instagram feeds. Use 1:1 (1080×1080) for images, or 4:5 (1080×1350) for video.',
  );
}

/**
 * The warning shown for a single existing 9:16 Meta creative (card tooltip and
 * preview alert).
 *
 * @param t - Translation function.
 * @returns The localized per-creative warning.
 */
export function getMetaPortraitCropWarning(t: TFunction, feeds: TMetaFeedPlacement[]): string {
  return t(
    'This creative is cropped in {{feeds}}. If important elements are cut off, disable it and use 1:1 (1080×1080).',
    { feeds: getMaskedFeedsLabel(t, feeds) },
  );
}

/**
 * The count-neutral body for the review-step warning. The count lives in the
 * title, so this reads the same for one or many creatives.
 *
 * @param t - Translation function.
 * @returns The localized review-step body.
 */
export function getMetaPortraitReviewWarning(t: TFunction): string {
  return t(
    'Cropping may cut off important elements. Preview these on the Creatives step, then disable and replace any with noticeable cropping with 1:1 (1080×1080).',
  );
}

/** The feed placements a Meta creative can be cropped in. */
const META_FEED_PLACEMENTS: TMetaFeedPlacement[] = ['facebook_feed', 'instagram_feed'];

/**
 * The Meta feeds that crop this creative. A 4:5 image is cropped in the
 * Facebook feed only, where the link card masks it to a square; 9:16 is cropped
 * in both.
 *
 * @param creative - The creative to check.
 * @returns The affected feeds, empty for a creative no feed crops.
 */
export function getMaskedMetaFeeds(creative: DisplayCreative): TMetaFeedPlacement[] {
  if (creative.channel !== 'meta') {
    return [];
  }
  const width = creative.get('width');
  const height = creative.get('height');
  const mediaType = creative.isVideo ? 'video' : 'image';
  return META_FEED_PLACEMENTS.filter((placement) =>
    isMaskedInMetaFeed(width, height, { mediaType, placement }),
  );
}

/**
 * Whether either Meta feed crops this creative.
 *
 * @param creative - The creative to check.
 * @returns True for a Meta creative that a feed crops.
 */
export function isMaskedMetaCreative(creative: DisplayCreative): boolean {
  return getMaskedMetaFeeds(creative).length > 0;
}

/**
 * Counts the enabled, non-archived Meta creatives that a feed crops.
 *
 * @param creatives - The campaign's creatives.
 * @returns The number of affected Meta creatives.
 */
export function countMaskedMetaCreatives(creatives: DisplayCreative[]): number {
  return creatives.filter(
    (creative) =>
      !creative.get('is_archived') && creative.get('is_enabled') && isMaskedMetaCreative(creative),
  ).length;
}
